import { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  ChakraProvider,
  Divider,
  Flex,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Startgroup } from "./api/Dtos";
import { PUT } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import { PromptForConfirmationModal } from "./Modals/PromptForConfirmationModal";
import {
  dateTimeInMsToString,
  timeInMsDiffToString,
  timeInMsToString,
} from "./Services/ResultService";
import { useParticipantsFromBackend } from "./Services/ParticipantService";
import { buildStartgroupDisciplineLabels } from "./Services/StartgroupDisciplineLabels";
import { useStartgroupsFromBackend } from "./Services/StartgroupService";

export function SetStarttimePhone() {
  const globalContext = useGlobalContext();
  const { startgroups, setStartgroups, loading } = useStartgroupsFromBackend();
  const { participants } = useParticipantsFromBackend();
  const [time, setTime] = useState(Date.now());
  const [showFinished, setShowFinished] = useState(false);

  //console.log("User role:", globalContext.authorizedAs);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(Date.now());
    }, 500);

    return () => clearInterval(interval);
  }, []);

  if (!globalContext.competition) return <div>Ingen tävling är vald!</div>;
  if (loading) return <div>Laddar startgrupper...</div>;
  if (startgroups.length === 0) return <div>Inga startgrupper hittades.</div>;

  const activeStartgroups = startgroups.filter(isRunningStartgroup);
  const upcomingStartgroups = startgroups.filter((startgroup) => !hasStarted(startgroup));
  const finishedStartgroups = startgroups.filter(isFinishedStartgroup);
  const disciplineNamesByStartgroupId = buildStartgroupDisciplineLabels(
    globalContext.competition.disciplines,
    participants
  );

  const onUpdateStartgroup = (update: Startgroup) => {
    PUT.startgroup(update, globalContext.competition!.id).then(() => {
      setStartgroups((oldArray) =>
        oldArray.map((entry) => (entry.id === update.id ? update : entry))
      );
    });
  };

  return (
    <Box maxW="480px" marginX="auto" paddingBottom="32px">
      <Box
        position="sticky"
        top={0}
        zIndex={1}
        background="white"
        borderBottom="1px solid"
        borderColor="gray.200"
        paddingTop="12px"
        paddingBottom="12px"
      >
        <HStack justify="space-between" align="flex-end" spacing={3}>
          <Box>
            <Text fontSize="sm" color="gray.600" textTransform="uppercase">
              Startklocka mobil
            </Text>
            <Text fontSize="md" color="gray.700">
              {globalContext.competition.tag}
            </Text>
          </Box>
          <ChakraProvider>
            <Text fontSize="4xl" fontWeight="bold" lineHeight="1">
              {timeInMsToString(time)}
            </Text>
          </ChakraProvider>
        </HStack>
      </Box>

      <VStack align="stretch" spacing={4} marginTop="16px">
        <StartgroupSection
          title={`Pågår nu (${activeStartgroups.length})`}
          emptyText="Ingen startgrupp är igång just nu."
          startgroups={activeStartgroups}
          currentTimeInMs={time}
          disciplineNamesByStartgroupId={disciplineNamesByStartgroupId}
          onUpdateStartgroup={onUpdateStartgroup}
        />

        <StartgroupSection
          title={`Nästa starter (${upcomingStartgroups.length})`}
          emptyText="Alla startgrupper har redan startats."
          startgroups={upcomingStartgroups}
          currentTimeInMs={time}
          disciplineNamesByStartgroupId={disciplineNamesByStartgroupId}
          onUpdateStartgroup={onUpdateStartgroup}
        />

        <Button
          variant="outline"
          size="lg"
          onClick={() => setShowFinished((old) => !old)}
        >
          {showFinished
            ? `Dölj avslutade (${finishedStartgroups.length})`
            : `Visa avslutade (${finishedStartgroups.length})`}
        </Button>

        {showFinished && (
          <StartgroupSection
            title={`Avslutade (${finishedStartgroups.length})`}
            emptyText="Inga avslutade startgrupper."
            startgroups={finishedStartgroups}
            currentTimeInMs={time}
            disciplineNamesByStartgroupId={disciplineNamesByStartgroupId}
            onUpdateStartgroup={onUpdateStartgroup}
          />
        )}
      </VStack>
    </Box>
  );
}

function StartgroupSection(props: {
  title: string;
  emptyText: string;
  startgroups: Array<Startgroup>;
  currentTimeInMs: number;
  disciplineNamesByStartgroupId: Record<number, Array<string>>;
  onUpdateStartgroup: (update: Startgroup) => void;
}) {
  return (
    <Box>
      <Text fontSize="xl" fontWeight="bold" marginBottom="8px">
        {props.title}
      </Text>
      <VStack align="stretch" spacing={3}>
        {props.startgroups.length === 0 ? (
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            padding="16px"
            background="gray.50"
          >
            <Text color="gray.600">{props.emptyText}</Text>
          </Box>
        ) : (
          props.startgroups.map((startgroup) => (
            <StartgroupPhoneCard
              key={startgroup.id}
              startgroup={startgroup}
              disciplineNames={
                props.disciplineNamesByStartgroupId[startgroup.id] ?? []
              }
              currentTimeInMs={props.currentTimeInMs}
              onUpdateStartgroup={props.onUpdateStartgroup}
            />
          ))
        )}
      </VStack>
    </Box>
  );
}

function StartgroupPhoneCard(props: {
  disciplineNames: Array<string>;
  startgroup: Startgroup;
  currentTimeInMs: number;
  onUpdateStartgroup: (update: Startgroup) => void;
}) {
  const running = isRunningStartgroup(props.startgroup);
  const finished = isFinishedStartgroup(props.startgroup);
  const elapsedTime = getElapsedTime(props.startgroup, props.currentTimeInMs);
  const globalContext = useGlobalContext();

  return (
    <Box
      border="1px solid"
      borderColor={finished ? "green.300" : running ? "yellow.300" : "gray.200"}
      borderRadius="xl"
      padding="16px"
      background={finished ? "green.50" : running ? "yellow.50" : "white"}
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={3}>
        <Flex justify="space-between" align="flex-start" gap={3}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              Startgrupp {props.startgroup.id}
            </Text>
            <Text color="gray.600">
              {props.disciplineNames.length > 0
                ? props.disciplineNames.join(", ")
                : "Ingen disciplin kopplad"}
            </Text>
          </Box>
          <Badge
            colorScheme={finished ? "green" : running ? "yellow" : "gray"}
            paddingX="10px"
            paddingY="6px"
            borderRadius="full"
            fontSize="0.9em"
          >
            {finished ? "Avslutad" : running ? "Igång" : "Ej startad"}
          </Badge>
        </Flex>

        <Divider />

        <Box>
          <Text fontSize="sm" color="gray.600">
            Planerad start
          </Text>
          <Text fontSize="xl" fontWeight="semibold">
            {timeInMsToString(props.startgroup.starttime_planned)}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {dateTimeInMsToString(props.startgroup.starttime_planned)}
          </Text>
        </Box>

        <HStack align="stretch" spacing={3}>
          <TimeFact
            label="Start"
            value={timeInMsToString(props.startgroup.starttime_actual)}
          />
          <TimeFact
            label="Stopp"
            value={
              props.startgroup.closetime_actual
                ? timeInMsToString(props.startgroup.closetime_actual)
                : `ca ${timeInMsToString(props.startgroup.closetime_planned)}`
            }
          />
        </HStack>

        <Box
          borderRadius="lg"
          background="black"
          color="white"
          padding="14px"
          textAlign="center"
        >
          <Text fontSize="sm" color="gray.300">
            Klocka
          </Text>
          <Text fontSize="3xl" fontWeight="bold">
            {elapsedTime}
          </Text>
        </Box>

        <ButtonGroup display="grid" gridTemplateColumns="1fr" gap={3}>
          <MobileVerifyButton
            colorScheme="green"
            label={running ? "Startad" : finished ? "Starta igen" : "Starta nu"}
            disable={running}
            verifyIfAndOnlyIf={true}
            verifyQuestion={finished ? "Vill du starta klockan igen?" : "Vill du starta klockan nu?"}
            onVerifiedClick={() => {
              if (!hasStarted(props.startgroup)) {
                props.onUpdateStartgroup({
                  ...props.startgroup,
                  starttime_actual: new Date().getTime(),
                });
              } else if (finished) {
                props.onUpdateStartgroup({
                  ...props.startgroup,
                  closetime_actual: null,
                });
              }
            }}
          />

          <ButtonGroup size="lg" display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <MobileVerifyButton
              colorScheme="orange"
              label="Stopp"
              disable={!hasStarted(props.startgroup) || finished}
              verifyIfAndOnlyIf={false}
              verifyQuestion="Är du säker på att du vill sätta en ny stopptid?"
              onVerifiedClick={() => {
                props.onUpdateStartgroup({
                  ...props.startgroup,
                  closetime_actual: new Date().getTime(),
                });
              }}
            />

            <MobileVerifyButton
              colorScheme="red"
              label="Återställ"
              disable={!hasStarted(props.startgroup) || !finished || globalContext.authorizedAs !== "ROLE_ADMIN"}
              verifyIfAndOnlyIf={true}
              verifyQuestion="Återställ, vill du radera start och stopptid?"
              onVerifiedClick={() => {
                props.onUpdateStartgroup({
                  ...props.startgroup,
                  starttime_actual: null,
                  closetime_actual: null,
                });
              }}
            />
          </ButtonGroup>
        </ButtonGroup>
      </VStack>
    </Box>
  );
}

function TimeFact(props: { label: string; value: string }) {
  return (
    <Box flex={1} borderRadius="lg" background="gray.50" padding="12px">
      <Text fontSize="sm" color="gray.600">
        {props.label}
      </Text>
      <Text fontSize="xl" fontWeight="semibold">
        {props.value}
      </Text>
    </Box>
  );
}

function MobileVerifyButton(props: {
  colorScheme: string;
  label: string;
  verifyQuestion: string;
  verifyIfAndOnlyIf: boolean;
  disable: boolean;
  onVerifiedClick: () => void;
}) {
  const [showPrompt, setShowPrompt] = useState(false);

  return (
    <>
      <Button
        size="lg"
        width="100%"
        colorScheme={props.colorScheme}
        isDisabled={props.disable}
        onClick={() => {
          if (props.disable) {
            return;
          }

          if (props.verifyIfAndOnlyIf) {
            setShowPrompt(true);
            return;
          }

          props.onVerifiedClick();
        }}
      >
        {props.label}
      </Button>

      {showPrompt && (
        <PromptForConfirmationModal
          colorScheme={props.colorScheme}
          onConfirm={() => {
            props.onVerifiedClick();
            setShowPrompt(false);
          }}
          onClose={() => {
            setShowPrompt(false);
          }}
          request={props.verifyQuestion}
        />
      )}
    </>
  );
}

function hasStarted(startgroup: Startgroup) {
  return startgroup.starttime_actual != null && startgroup.starttime_actual !== 0;
}

function isFinishedStartgroup(startgroup: Startgroup) {
  return (
    startgroup.closetime_actual != null && startgroup.closetime_actual !== 0
  );
}

function isRunningStartgroup(startgroup: Startgroup) {
  return hasStarted(startgroup) && !isFinishedStartgroup(startgroup);
}

function getElapsedTime(startgroup: Startgroup, currentTimeInMs: number) {
  if (!hasStarted(startgroup)) {
    return "Ej startad";
  }

  if (isRunningStartgroup(startgroup)) {
    return timeInMsDiffToString(
      currentTimeInMs,
      truncateToSecond(startgroup.starttime_actual!)
    );
  }

  return timeInMsDiffToString(
    truncateToSecond(startgroup.closetime_actual!),
    truncateToSecond(startgroup.starttime_actual!)
  );
}

function truncateToSecond(timeInMs: number) {
  return Math.trunc(timeInMs / 1000) * 1000;
}

export default SetStarttimePhone;